import type { IAdBreakData } from '../../../helpers/CDNHandler';
import { avodMixpanelEvents } from '../../../helpers/avodHandler';
import playerService from '../../../helpers/bitmovinPlayer';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import { AdBreakEvent, AdEvent, PlaybackEvent, PlayerEvent } from 'bitmovin-player';
import { getLastCSAISupplyParameters } from '../../../hooks/useCSAI';
import { getConfiguration } from '../../../hooks/useConfiguration';
import { NO_AD_CTA_AD_TYPES } from '../constants';
import {
    hideNoAdCta,
    isClickOnNoAdCta,
    setNoAdCtaAdBreakStartTime,
    setupNoAdCtaFromConfig,
    triggerNoAdCtaAction,
} from '../utils/AdsUiUtils';

/**
 * CSAI Ad Events Handlers
 */
const getAdEventsHandlers = () => {
    let isAdStarted = false;
    let adsUiElement: HTMLElement | null = null;

    /**
     * Handles click events on ad overlays by toggling playback state.
     * Prevents event propagation to parent elements to disable click-through behavior (opening external links).
     *
     * @param e - The mouse event triggered on the ad overlay
     */
    const adOverlayClickHandler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        // Let control bar work: fullscreen, volume, seekbar
        if (
            target?.closest?.(
                '[class*="controlbar"], [class*="fullscreentoggle"], [class*="volumetoggle"], [class*="volumeslider"], [class*="seekbar"]'
            )
        ) {
            return;
        }

        // No Ads CTA: when overlay is on top (e.g. fullscreen), e.target is the overlay.
        // Check if click coords are within CTA bounds and trigger upgrade modal.
        if (isClickOnNoAdCta(e.clientX, e.clientY)) {
            e.preventDefault();
            e.stopPropagation();
            triggerNoAdCtaAction();
            return;
        }
        // Direct CTA clicks when button receives the event
        if (target?.closest?.('.ui-no-ad-cta-button')) {
            return;
        }

        const player = playerService.player;

        if (player?.ads.getActiveAd()) {
            e.preventDefault();
            e.stopPropagation();
            if (player.isPlaying()) {
                player.pause();
            } else {
                safePlayerPlay(player);
            }
        }
    };

    /**
     * Disables click-through behavior for advertisements by intercepting click events.
     * Attach to #ads-ui (capture) so we catch clicks even when overlays are on top (e.g. fullscreen).
     * @todo Move to AdsUIUtils?
     */
    const disableAdClickThrough = () => {
        setTimeout(() => {
            const adsUi = document.getElementById('ads-ui');
            const player = playerService.player;
            if (!adsUi || !player) return;

            adsUiElement = adsUi;
            // Use ads-ui with capture so we intercept before any overlay; handles fullscreen where overlays block CTA
            adsUi.addEventListener('click', adOverlayClickHandler, true);
        }, 1000);
    };

    return {
        [PlayerEvent.AdStarted]: function (data: AdEvent) {
            if (!data.ad) return;

            playerService.setAdsData(data.ad);
            avodMixpanelEvents.onAdStart();
            isAdStarted = true;

            disableAdClickThrough();
        },
        [PlayerEvent.AdFinished]: function (data: AdEvent) {
            if (!data.ad) return;
            avodMixpanelEvents.onAdCompleted();

            adsUiElement?.removeEventListener('click', adOverlayClickHandler);
            adsUiElement = null;
        },

        onTimeChanged: function () {
            const isPlayingCSAIAd = !!playerService.player?.ads.getActiveAd();

            if (isAdStarted && !isPlayingCSAIAd) {
                avodMixpanelEvents.onVideoStarted();
                isAdStarted = false;
            }
        },

        [PlayerEvent.AdQuartile]: function (data) {
            void data;
        },

        /** No Ads CTA: hide button and clear ad break start time when CSAI ad break ends */
        [PlayerEvent.AdBreakFinished]: function () {
            // TODO: add ad type - 'preroll', 'midroll'
            hideNoAdCta();
            setNoAdCtaAdBreakStartTime(null);
        },

        /** No Ads CTA: CSAI is always preroll. Store start time, set ads data, show button if enabled in config */
        [PlayerEvent.AdBreakStarted]: function (data: AdBreakEvent) {
            const t = playerService.player?.getCurrentTime() ?? 0;
            setNoAdCtaAdBreakStartTime(typeof t === 'number' ? t : 0);
            playerService.setAdsData({
                ...(data.adBreak as object),
                adType: NO_AD_CTA_AD_TYPES.PREROLL,
            } as unknown as IAdBreakData);
            setupNoAdCtaFromConfig(getConfiguration(), NO_AD_CTA_AD_TYPES.PREROLL);
        },

        [PlayerEvent.AdSkipped]: function (data) {
            void data;
        },

        [PlayerEvent.AdManifestLoaded]: function (data) {
            const advertisingParameters = getLastCSAISupplyParameters() || {};

            avodMixpanelEvents.onAdReceived(data, advertisingParameters, 'CSAI', 'linear');
        },
    };
};

const adEventsHandlers = getAdEventsHandlers();

export default adEventsHandlers;
